"use client";

import { useState } from "react";
import { Check, MessageSquare, Sparkles, Zap, Plus, Minus, ArrowRight } from "lucide-react";
import { ScrollReveal } from "@/components/ui/scroll-reveal";
import { WebsiteCtaLink } from "@/app/(website)/_components/website-cta-link";

const faqs = [
  {
    question: "How do you calculate monthly orders?",
    answer: "We calculate your average monthly orders by looking at the total number of orders placed over the past 3 months up to your billing date, then dividing by three. This ensures a fair and consistent assessment of your usage."
  },
  {
    question: "How frequently is my data updated?",
    answer: "Your store’s data is refreshed on demand - any time you request it. This gives you and your team an always up-to-date view of your business performance and customer behavior."
  },
  {
    question: "What do I get in my 30-day free trial?",
    answer: "The 30-day free trial gives you access to all features in the Starter plan."
  },
  {
    question: "Will I be charged after my trial is finished?",
    answer: "No. You can try our platform free for 30 days - no credit card required, no strings attached. Once your trial ends, it’s entirely up to you whether to continue. You’ll only be charged if you choose to upgrade."
  },
  {
    question: "How do I know which plan is right for my business?",
    answer: "Check our pricing. We analyze your average orders across stores and recommend the ideal plan for your needs. If your monthly order volume exceeds your current plan, your account will be automatically upgraded, and you’ll be notified by email."
  },
  {
    question: "Can I change my plan after signing up?",
    answer: "Yes! You can change your plan once you are subscribed by accessing your account settings within the platform and choosing the plan that works for you."
  },
  {
    question: "How long does the setup take?",
    answer: "Connecting each store and additional data sources takes less than 5 minutes. All features - including your dashboard - will be immediately available. You can also add more dashboards anytime by selecting and pinning charts from chat answers."
  },
  {
    question: "Is my store data secure?",
    answer: "Yes. We connect securely to your WooCommerce store in read-only mode, with full encryption and GDPR compliance. Your data stays private, never used for public AI training, and you can disconnect or delete it at any time. Each store has its own isolated AI context to ensure personalized and secure insights.\n\nOur website and servers use HTTPS over SSL to protect your data. All information from your account is used solely to analyze and improve your account experience - never for anything else.\n\nWe use the Vultr Trust Center as our Trusted Cloud Infrastructure for Security and Compliance."
  },
  {
    question: "What makes us different from competitors?",
    answer: "We’re not just another analytics dashboard - we’re the first conversational commerce intelligence platform. Unlike others, we let you chat directly with your WooCommerce data, receive personalized answers tailored to your store, and get soft, AI-driven recommendations to boost profit and efficiency. No setup headaches, no guesswork - just instant clarity, built for stores that need to spot inefficiencies before they hurt revenue or cash flow."
  },
  {
    question: "What happens when my revenue grows?",
    answer: "As your business grows, you’ll automatically be moved to the appropriate plan, and we’ll notify you well in advance. Note: If you’re on an annual pre-paid plan, your current tier is locked in for the full year, even as your business scales."
  },
  {
    question: "Do I need to sign a contract?",
    answer: "The pricing displayed on this page reflects our 12-month annual plans, which offer significant discounts and can be paid either upfront or monthly. For added flexibility, we also offer month-to-month pricing with no long-term commitment."
  },
  {
    question: "Do your prices include tax fees?",
    answer: "Our plans are exclusive of tax fees."
  },
  {
    question: "Where is your support located?",
    answer: "Our HQ is in Delaware, USA with some of our team spread out across other regions."
  },
  {
    question: "Is my historical order data included?",
    answer: "We use all WooCommerce's historical orders."
  }
];

function FAQItem({ question, answer, isOpen, onClick }: { question: string, answer: string, isOpen: boolean, onClick: () => void }) {
  return (
    <div className="border-b border-slate-800">
      <button
        onClick={onClick}
        className="w-full py-6 flex items-center justify-between text-left hover:text-purple-400 transition-colors"
      >
        <span className="text-xl font-medium pr-8">{question}</span>
        {isOpen ? (
          <Minus className="h-6 w-6 text-purple-500 shrink-0" />
        ) : (
          <Plus className="h-6 w-6 text-purple-500 shrink-0" />
        )}
      </button>
      <div 
        className={`overflow-hidden transition-all duration-300 ease-in-out ${
          isOpen ? 'max-h-[500px] mb-6 opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <p className="text-lg text-slate-400 leading-relaxed whitespace-pre-line">
          {answer}
        </p>
      </div>
    </div>
  );
}

export default function PricingPage() {
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  return (
    <div className="relative py-20 md:py-32 overflow-hidden">
      {/* Background Glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-[600px] bg-purple-500/5 blur-[120px] -z-10" />
      
      <div className="container mx-auto px-4 md:px-6">
        <div className="max-w-4xl mx-auto text-center mb-20">
          <ScrollReveal>
            <div className="inline-flex items-center space-x-2 bg-purple-500/10 border border-purple-500/20 rounded-full px-4 py-1.5 mb-8">
              <Sparkles className="h-4 w-4 text-purple-400" />
              <span className="text-purple-300 text-base font-medium">Tailored for your growth</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold mb-8 tracking-tight bg-gradient-to-b from-white to-slate-500 bg-clip-text text-transparent">
              Pricing that scales with you.
            </h1>
            <p className="text-xl md:text-2xl text-slate-400 leading-relaxed max-w-2xl mx-auto mb-12">
              Start your free 30-day trial - see how much revenue and profit you’ve been leaving on the table.
              <br />
              <span className="text-lg md:text-xl opacity-80 mt-4 block">
                Your total monthly order volume determines your plan - no surprises, no hidden fees.
              </span>
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <WebsiteCtaLink href="/login" className="w-full sm:w-auto" fullWidth>
                Start Now
              </WebsiteCtaLink>
            </div>
          </ScrollReveal>
        </div>

        <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto mb-32">
          {[
            {
              icon: <Zap className="h-8 w-8 text-purple-400" />,
              title: "Instant Setup",
              description: "Connect your WooCommerce store in minutes and start chatting with your data immediately."
            },
            {
              icon: <MessageSquare className="h-8 w-8 text-purple-400" />,
              title: "AI-Powered Insights",
              description: "Get answers to complex questions and discover hidden inefficiencies with our advanced AI agents."
            },
            {
              icon: <Check className="h-8 w-8 text-purple-400" />,
              title: "Enterprise Ready",
              description: "Secure, scalable, and built to handle high-volume stores with custom requirements."
            }
          ].map((feature, i) => (
            <ScrollReveal key={i} delay={i * 100}>
              <div className="p-8 rounded-3xl bg-slate-900/50 border border-slate-800 hover:border-purple-500/30 transition-all h-full">
                <div className="mb-6 p-3 rounded-2xl bg-slate-950 border border-slate-800 w-fit">
                  {feature.icon}
                </div>
                <h3 className="text-2xl font-bold mb-3 text-white">{feature.title}</h3>
                <p className="text-lg text-slate-400 leading-relaxed">{feature.description}</p>
              </div>
            </ScrollReveal>
          ))}
        </div>

        <ScrollReveal delay={400}>
          <div className="max-w-4xl mx-auto text-center mb-32">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">What makes us different?</h2>
            <p className="text-2xl text-purple-400 font-medium mb-4">Dashboards only show you numbers. Storita shows you the truth.</p>
            <p className="text-xl text-slate-400 leading-relaxed max-w-3xl mx-auto">
              We uncover the hidden inefficiencies silently draining your profits - and tell you exactly what to do next.
            </p>
          </div>
        </ScrollReveal>

        <ScrollReveal delay={500}>
          <div className="max-w-4xl mx-auto mb-32">
            <h2 className="text-4xl md:text-5xl font-bold mb-12 text-center text-white">FAQ</h2>
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <FAQItem
                  key={index}
                  question={faq.question}
                  answer={faq.answer}
                  isOpen={openFaqIndex === index}
                  onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                />
              ))}
            </div>
          </div>
        </ScrollReveal>

        <ScrollReveal delay={600}>
          <div className="max-w-6xl mx-auto mb-20">
            <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center text-white">Trusted by Growing Stores</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {/* IVFPharmacy */}
              <div className="p-8 rounded-3xl bg-slate-900/40 border border-slate-800 backdrop-blur-sm flex flex-col justify-between hover:border-purple-500/30 transition-all group">
                <p className="text-slate-300 text-lg leading-relaxed mb-8 italic">
                  "Storita’s platform delivers an AI business consultant who lives in our data, understands our market and delivers crucial, valuable business insights."
                </p>
                <a 
                  href="https://www.ivfpharmacy.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-purple-400 font-bold hover:text-purple-300 transition-colors"
                >
                  IVFPharmacy.com
                  <ArrowRight className="ml-2 h-4 w-4 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                </a>
              </div>

              {/* DXRacer */}
              <div className="p-8 rounded-3xl bg-slate-900/40 border border-slate-800 backdrop-blur-sm flex flex-col justify-between hover:border-purple-500/30 transition-all group">
                <p className="text-slate-300 text-lg leading-relaxed mb-8 italic">
                  "Storita lets us understand our store through a simple WhatsApp chat - quick answers, no digging through analytics."
                </p>
                <a 
                  href="https://dxracer.co.il/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-purple-400 font-bold hover:text-purple-300 transition-colors"
                >
                  Dxracer.co.il
                  <ArrowRight className="ml-2 h-4 w-4 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                </a>
              </div>

              {/* Soltam */}
              <div className="p-8 rounded-3xl bg-slate-900/40 border border-slate-800 backdrop-blur-sm flex flex-col justify-between hover:border-purple-500/30 transition-all group">
                <p className="text-slate-300 text-lg leading-relaxed mb-8 italic">
                  "Identifying non-selling products required digging through reports, so it rarely happened. Now we see them immediately."
                </p>
                <a 
                  href="https://soltam.co.il/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-purple-400 font-bold hover:text-purple-300 transition-colors"
                >
                  Soltam.co.il
                  <ArrowRight className="ml-2 h-4 w-4 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                </a>
              </div>
            </div>
          </div>
        </ScrollReveal>
      </div>

      {/* 
        PREVIOUS PRICING PLANS (Preserved for future use)
        
        const plans = [
          {
            name: "Free",
            price: 0,
            description: "For new stores getting started.",
            features: ["Up to 50 orders/month", "1 Seat", "AI Generated Questions", "Instant Dashboards", "Chat with Data", "Email Support"],
            cta: "Start Free",
            highlight: false
          },
          {
            name: "Starter",
            price: 29,
            description: "For stores doing < $20k / month.",
            features: ["Up to 200 orders/month", "1 Seat", "AI Generated Questions", "Instant Dashboards", "Chat with Data", "AI Recommendations", "Store's Performance", "Email Support"],
            cta: "Start Free Trial",
            highlight: false
          },
          {
            name: "Growth",
            price: 79,
            description: "For stores doing $20k – $100k / month.",
            features: ["201 – 1,000 orders/month", "3 Seats", "AI Generated Questions", "Instant Dashboards", "Chat with Data", "AI Recommendations", "Store's Performance", "Priority Email Support"],
            cta: "Get Started",
            highlight: false
          },
          {
            name: "Pro",
            price: 149,
            description: "For stores doing $100k – $300k / month.",
            features: ["1,001 – 5,000 orders/month", "7 Seats", "AI Generated Questions", "Instant Dashboards", "Chat with Data", "AI Recommendations", "Store's Performance", "Priority Email + Chat Support"],
            cta: "Get Started",
            highlight: true
          },
          {
            name: "Enterprise",
            price: "Custom",
            description: "For stores doing $12M – $100M yearly.",
            features: ["20,001+ orders/month", "Custom Seats", "Dedicated Account Manager", "Custom AI Training", "SLA & Security Audit", "White-label Options"],
            cta: "Contact Sales",
            highlight: false
          }
        ];
      */}
    </div>
  );
}
